import * as React from "react"
import { debounce } from "lodash"

import { TextInput } from "@tenforce/product-fields"

import { GlobalState } from "../../../../store"
import { useAppDispatch, useAppSelector } from "../../../../hooks"

import { ITextField } from "../.."
import { updateField } from "../../slice"

import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { getAutoCompleteSuggestions } from "../../../../apis/autoComplete"
import { addErrorNotification } from "../../../notifications/actions"
import { Labels } from "../../../../util/Translations"

export interface ITextFieldProps {
    field: ITextField
    extraClasses?: string
    hideTitle?: boolean
    isParentLoading?: boolean
}

type Props = ITextFieldProps

export const TextField = ({
    field,
    hideTitle,
    extraClasses,
    isParentLoading,
}: Props): JSX.Element => {
    const dispatch = useAppDispatch()

    const { isLoading: isFieldLoading } = useAppSelector(
        (state: GlobalState) => state.fields,
    )
    const { loadingResources } = useAppSelector(
        (state: GlobalState) => state.resources,
    )

    const {
        hasMultipleLines,
        isAutoCompleteEnabled,
        position: _position,
        odataType: _odataType,
        value,
        itemId,
        maxLength,
        ...fieldProps
    } = field

    const {
        ClickHereToEdit,
        RequiredFieldErrorMessage,
        GetTextSuggestionError,
    } = Labels

    const [suggestions, setSuggestions] = React.useState<string[]>([])
    const [isFocused, setIsFocused] = React.useState<boolean>(false)
    const revertValueFunc = React.useRef<() => void | null>()

    // in case we are waiting for the field data to be loaded
    // or resources to be loaded for the internal links
    const isLoading =
        isParentLoading || isFieldLoading || loadingResources.length > 0

    React.useEffect(() => {
        if (value && value.length > maxLength) {
            return
        }
        if (isAutoCompleteEnabled && isFocused) {
            getSuggestions(value ?? "")
        }
    }, [value, isFocused, isAutoCompleteEnabled, maxLength])

    const setRevertValue = (resetValue: () => void | null): void => {
        revertValueFunc.current = resetValue
    }

    const setSuggestionsByValue = async (inputValue: string): Promise<void> => {
        setSuggestions(
            await getAutoCompleteSuggestions(
                itemId,
                field.fieldId,
                inputValue ?? "",
                undefined,
                undefined,
                GetTextSuggestionError,
            ),
        )
    }

    const getSuggestions = async (inputValue: string): Promise<void> => {
        try {
            // This syncs the input-hook with the field value
            if (inputValue.length > maxLength) {
                revertValueFunc.current?.()
                setSuggestionsByValue(value ?? "")
                return
            }
            setSuggestionsByValue(inputValue)
        } catch (error) {
            dispatch(addErrorNotification(error.message))
        }
    }

    const handleGenerateSuggestions = React.useCallback(
        debounce(getSuggestions, 300),
        [getSuggestions, debounce],
    )

    const modifiedProps = hideTitle
        ? { ...fieldProps, title: "", description: "" }
        : fieldProps
    return (
        <TextInput
            {...modifiedProps}
            suggestions={suggestions}
            onValueChanged={
                isAutoCompleteEnabled ? handleGenerateSuggestions : undefined
            }
            maxLength={maxLength}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            extraClasses={`${extraClasses} tw-w-full`}
            autoSize={hasMultipleLines}
            formatValue={(value: string): JSX.Element => {
                return (
                    <HTMLAndInternalLinkParser
                        content={value}
                        parseHTML={false}
                    />
                )
            }}
            isLoading={isLoading}
            isTextArea={hasMultipleLines}
            mandatoryErrorText={RequiredFieldErrorMessage}
            onSave={(newValue: string): void => {
                if (newValue !== value) {
                    dispatch(
                        updateField({
                            field,
                            newValue,
                        }),
                    )
                }
            }}
            placeholder={ClickHereToEdit}
            // value can be null, in case it was never set
            value={value ?? ""}
            onFocus={(): void => setIsFocused(true)}
            onBlur={(): void => setIsFocused(false)}
            revertValue={setRevertValue}
        />
    )
}

export default TextField
